import React from "react";
import { useTranslation } from "react-i18next";
import { useBranding } from "@/contexts/BrandingProvider";

// # WHY: mirrors ContactDetailsPopup.whatsappHref — backend persists digits-only
// (see OwnerBrandingManager.normalizeWhatsapp), but be defensive about a manually
// edited dashboard value that slipped a "+" or whitespace through before persistence.
function whatsappHref(value: string): string {
  const digits = value.replace(/\D/g, "");
  return `https://wa.me/${digits}`;
}

export const FloatingWhatsAppButton: React.FC = () => {
  const { t } = useTranslation();
  const { branding, isBranded } = useBranding();

  if (!isBranded || !branding) return null;
  if (!branding.floatingWhatsappEnabled) return null;
  if (!branding.contactWhatsapp) return null;

  const href = whatsappHref(branding.contactWhatsapp);
  const label = t("branding.floatingWhatsapp.label", "Chat on WhatsApp");

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid="floating-whatsapp-button"
      className="fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg ring-1 ring-black/10 transition-transform hover:scale-105 active:scale-95 sm:bottom-6 sm:right-6 pb-[max(env(safe-area-inset-bottom),0px)]"
    >
      {/* WhatsApp glyph (inline SVG) — lucide ships MessageCircle, not the WhatsApp mark; we
          inline a single 24px path so the floating affordance is unambiguous. */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 32 32"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M19.11 17.21c-.27-.13-1.59-.78-1.84-.87-.25-.09-.43-.13-.61.14-.18.27-.7.87-.86 1.05-.16.18-.32.2-.59.07-.27-.13-1.13-.42-2.16-1.33-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.41.12-.55.13-.13.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.3 0 1.36.98 2.67 1.12 2.85.14.18 1.93 2.94 4.67 4.13.65.28 1.16.45 1.55.58.65.21 1.24.18 1.71.11.52-.08 1.59-.65 1.81-1.27.22-.62.22-1.16.16-1.27-.07-.11-.25-.18-.52-.32z" />
        <path d="M26.69 5.34A13.42 13.42 0 0 0 16.05 1C8.31 1 2.05 7.26 2.05 15c0 2.45.64 4.84 1.86 6.95L2 31l9.27-2.43a14 14 0 0 0 4.78.83h.01C23.79 29.4 30 23.14 30 15.4a13.4 13.4 0 0 0-3.31-10.06zM16.05 27a11.6 11.6 0 0 1-5.92-1.62l-.42-.25-5.5 1.44 1.46-5.36-.28-.44A11.55 11.55 0 0 1 27.62 15.4 11.6 11.6 0 0 1 16.05 27z" />
      </svg>
    </a>
  );
};

export default FloatingWhatsAppButton;
